'use strict';
import get from 'lodash/get';
import {createEventHandler} from 'recompose';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/last';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/withLatestFrom';
import {getId} from '../../../core-utilities/domain/ReduxUtils';
import {ButtonState} from '../../../core-utilities/enums/ButtonState';
import {createToggleState} from '../../../core-utilities/react-utils';
import {requestToButtonState} from '../../../core-utilities/redux-request/utils';
import {buildPropsByCollection} from '../../redux/utils';
import {buildGoBack} from '../../utils';


/**
 * @param {Object} config
 * @param {String} config.collection
 * @param {String} config.taskListState
 * @param {String} config.taskDetailsState
 * @param {ReduxRequestModal} config.updateReduxRequestModal
 * @param {ReduxRequest} config.updateResponseReduxRequest
 */
export const RequirementViewFormContainer = (config) => (state$, dispatch) => (props$) => {
  const {
    collection,
    taskListState,
    taskDetailsState,
    updateReduxRequestModal,
    updateResponseReduxRequest,
  } = config;

  /** @type {ReduxRequestActions} */
  const updateFormActions = updateResponseReduxRequest.actions;

  /** @type {ReduxRequestOptions} */
  const updateFormOptions = updateResponseReduxRequest.options;

  const {
    stateParams$,
    requirement$,
    breadcrumbPath$,
  } = buildPropsByCollection(collection, state$, props$);

  const onClickEdit = createEditEvent(updateReduxRequestModal, requirement$, dispatch, props$);
  const onGoBack = buildGoBack({
    props$,
    requirement$,
    taskListState,
    taskDetailsState,
    dispatch,
  });

  const {
    handler: onSaveForm,
    stream: saveForm$,
  } = createEventHandler();

  const {
    handler: onTriggerSaveForm,
    stream: triggerSaveForm$,
  } = createEventHandler();

  const {
    handler: onIsFormDirtyChange,
    stream: isFormDirty$,
  } = createToggleState(props$);

  const {
    handler: onToggleEditingForm,
    stream: isEditingForm$,
  } = createToggleState(props$);

  // Reset form response update state
  props$
    .map(props => getId(props.requirement))
    .distinctUntilChanged()
    .withLatestFrom(isEditingForm$)
    .filter(([_, isEditing]) => !!isEditing)
    .takeUntil(props$.last())
    .subscribe(() => {
      onToggleEditingForm();
      dispatch(updateFormActions.resetRequest());
    });

  const updateFormMode$ = state$
    .map(updateFormOptions.getState)
    .map(requestToButtonState)
    .distinctUntilChanged();

  // reset edit mode when form returns to start state after success
  updateFormMode$
    .distinctUntilChanged()
    .filter(mode => mode === ButtonState.SUCCESS)
    .switchMap(() =>
      updateFormMode$
        .filter(mode => mode === ButtonState.START)
        .take(1)
    )
    .withLatestFrom(isEditingForm$)
    .takeUntil(props$.last())
    .subscribe(([_, isEditingForm]) => isEditingForm && onToggleEditingForm());

  saveForm$
    .withLatestFrom(props$, requirement$, updateFormMode$)
    .takeUntil(props$.last())
    .subscribe(([data, props, requirement, updateFormMode]) => {
      if (updateFormMode !== ButtonState.START)
        return;

      const requirementId = getId(requirement);
      const formResponseId = getId(get(requirement, 'formResponse'));
      const formVersion = get(requirement, 'formResponse.formVersion');
      if (!requirementId || !formResponseId)
        return;

      dispatch(updateFormActions.sendRequest({
        data: {
          id: requirementId,
        },
        body: {
          formResponse: {
            id: formResponseId,
            formVersion,
            responses: data.responses,
          },
        },
      }));
    });

  return props$
    .combineLatest(
      stateParams$,
      requirement$,
      breadcrumbPath$,
      isEditingForm$,
      updateFormMode$,
      isFormDirty$,
      (props, stateParams, requirement, breadcrumbPath, isEditingForm, updateFormMode, isFormDirty) => ({
        ...props,
        parentId: get(stateParams, 'requirementId'),
        collection,
        // init: !!requirement,
        requirement,
        breadcrumbPath,

        isFormDirty,
        isEditingForm,
        updateFormMode,
        triggerSaveForm$,

        onGoBack,
        onClickEdit,
        onSaveForm,
        onToggleEditingForm,
        onTriggerSaveForm,
        onIsFormDirtyChange,
      }));
};

/**
 * @param {ReduxRequestModal} updateReduxRequestModal
 * @param {Observable} requirement$
 * @param {Function} dispatch
 * @param {Observable} props$
 */
function createEditEvent(updateReduxRequestModal, requirement$, dispatch, props$) {
  const {
    handler: onClickEdit,
    stream: edit$,
  } = createEventHandler();

  edit$
    .withLatestFrom(requirement$)
    .takeUntil(props$.last())
    .subscribe(([e, requirement]) => {
      e.stopPropagation();
      dispatch(updateReduxRequestModal.actions.showModal({model: requirement}));
    });

  return onClickEdit;
}



// WEBPACK FOOTER //
// ./src/js/requirements/components/RequirementViewForm/RequirementViewFormContainer.js